import { getCustomTrackLayout, registerCustomTrack, unregisterCustomTrack } from "../game/simulation/track";
import { CUSTOM_TRACK_ID_PATTERN, type CustomTrackId } from "../game/simulation/customTrackSchema";
import { normalizeCustomTrackDescriptor, type CustomTrackDescriptor } from "./protocol";

/** Keep detached race definitions bounded in a long-lived browser tab. */
export const MAX_RETAINED_CUSTOM_TRACKS = 8 as const;

export type CustomTrackDistributionErrorCode = "CUSTOM_TRACK_INVALID" | "CUSTOM_TRACK_CONFLICT" | "CUSTOM_TRACK_REGISTRATION";

/** Typed distribution failure; callers can surface it without an uncaught render exception. */
export class CustomTrackDistributionError extends Error {
  readonly code: CustomTrackDistributionErrorCode;
  readonly runtimeId: string | null;

  constructor(code: CustomTrackDistributionErrorCode, message: string, runtimeId: string | null = null, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "CustomTrackDistributionError";
    this.code = code;
    this.runtimeId = runtimeId;
  }
}

/** A malformed c_ id is still treated as a custom candidate and never as a built-in. */
export function isCustomTrackIdCandidate(value: unknown): value is string {
  return typeof value === "string" && (value.startsWith("c_") || value === "__editor__");
}

export function isCustomRuntimeTrackId(value: unknown): value is CustomTrackId {
  return typeof value === "string" && CUSTOM_TRACK_ID_PATTERN.test(value);
}

interface SharedRuntimeEntry {
  fingerprint: string;
  references: number;
  /** False when the server or another owner registered the runtime first. */
  ownedByDistribution: boolean;
}

// Multiple NetworkRaceClient/overlay instances can coexist in the same test
// document. Refcounting prevents one closing overlay from unregistering a
// definition still used by another consumer.
const sharedRuntimeEntries = new Map<CustomTrackId, SharedRuntimeEntry>();

function deepFreeze<T>(value: T): T {
  if (value === null || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  for (const key of Object.keys(value as object)) deepFreeze((value as Record<string, unknown>)[key]);
  return value;
}

function fingerprint(descriptor: CustomTrackDescriptor): string {
  return JSON.stringify({ runtimeId: descriptor.runtimeId, key: descriptor.key, revision: descriptor.revision, def: descriptor.def });
}

function cloneDescriptor(descriptor: CustomTrackDescriptor): CustomTrackDescriptor {
  // The protocol normalizer already detached the value. Freeze it before it
  // enters the registry so a UI cannot mutate the definition underneath the
  // simulation/renderers or alter duplicate/conflict comparisons.
  return deepFreeze({
    runtimeId: descriptor.runtimeId,
    key: descriptor.key,
    revision: descriptor.revision,
    def: descriptor.def,
  });
}

function globalDefinitionFingerprint(runtimeId: CustomTrackId): string | null {
  try {
    const layout = getCustomTrackLayout(runtimeId);
    return layout ? JSON.stringify(layout.definition) : null;
  } catch {
    return null;
  }
}

/**
 * Owns the browser-side lifetime of runtime track registrations. Definitions
 * are installed before a snapshot is accepted and are released only after the
 * active/previous transition references no longer need them.
 */
export class CustomTrackRuntimeRegistry {
  private readonly entries = new Map<CustomTrackId, CustomTrackDescriptor>();
  private activeRuntimeId: CustomTrackId | null = null;
  private previousRuntimeId: CustomTrackId | null = null;

  get size(): number {
    return this.entries.size;
  }

  has(runtimeId: string): boolean {
    return this.entries.has(runtimeId as CustomTrackId);
  }

  get(runtimeId: string): CustomTrackDescriptor | null {
    return this.entries.get(runtimeId as CustomTrackId) ?? null;
  }

  /** Register an untrusted wire value and return the immutable canonical copy. */
  register(value: unknown): CustomTrackDescriptor {
    let descriptor: CustomTrackDescriptor;
    try {
      descriptor = cloneDescriptor(normalizeCustomTrackDescriptor(value));
    } catch (error) {
      if (error instanceof CustomTrackDistributionError) throw error;
      const runtimeId = isCustomRuntimeTrackId((value as { runtimeId?: unknown } | null)?.runtimeId) ? (value as { runtimeId: CustomTrackId }).runtimeId : null;
      throw new CustomTrackDistributionError("CUSTOM_TRACK_INVALID", error instanceof Error ? error.message : "Invalid custom track definition", runtimeId, { cause: error });
    }

    const existing = this.entries.get(descriptor.runtimeId);
    const nextFingerprint = fingerprint(descriptor);
    if (existing) {
      if (fingerprint(existing) !== nextFingerprint) {
        throw new CustomTrackDistributionError("CUSTOM_TRACK_CONFLICT", `Custom track runtime ${descriptor.runtimeId} changed after registration`, descriptor.runtimeId);
      }
      this.touch(descriptor.runtimeId);
      return existing;
    }

    const shared = sharedRuntimeEntries.get(descriptor.runtimeId);
    if (shared) {
      if (shared.fingerprint !== nextFingerprint) {
        throw new CustomTrackDistributionError("CUSTOM_TRACK_CONFLICT", `Custom track runtime ${descriptor.runtimeId} conflicts with an existing registration`, descriptor.runtimeId);
      }
      shared.references += 1;
    } else {
      let ownedByDistribution = true;
      // The server-side registry may exist in the same process during tests.
      // Never replace an externally owned runtime id; verify its normalized
      // definition first and leave its lifetime to that owner.
      const existingDefinition = globalDefinitionFingerprint(descriptor.runtimeId);
      if (existingDefinition !== null) {
        if (existingDefinition !== JSON.stringify(descriptor.def)) {
          throw new CustomTrackDistributionError("CUSTOM_TRACK_CONFLICT", `Custom track runtime ${descriptor.runtimeId} conflicts with an existing registration`, descriptor.runtimeId);
        }
        ownedByDistribution = false;
      } else {
        try {
          registerCustomTrack(descriptor.runtimeId, descriptor.def);
        } catch (error) {
          throw new CustomTrackDistributionError("CUSTOM_TRACK_REGISTRATION", error instanceof Error ? error.message : "Could not register custom track", descriptor.runtimeId, { cause: error });
        }
      }
      sharedRuntimeEntries.set(descriptor.runtimeId, { fingerprint: nextFingerprint, references: 1, ownedByDistribution });
    }

    this.entries.set(descriptor.runtimeId, descriptor);
    this.prune();
    return descriptor;
  }

  /** Mark the track used by an accepted snapshot and retain its transition predecessor. */
  observeTrack(trackId: string): void {
    const next = isCustomRuntimeTrackId(trackId) && this.entries.has(trackId) ? trackId : null;
    if (next !== this.activeRuntimeId) {
      this.previousRuntimeId = this.activeRuntimeId;
      this.activeRuntimeId = next;
    }
    if (next) this.touch(next);
    this.prune();
  }

  clear(): void {
    for (const runtimeId of [...this.entries.keys()]) this.release(runtimeId);
    this.entries.clear();
    this.activeRuntimeId = null;
    this.previousRuntimeId = null;
  }

  private touch(runtimeId: CustomTrackId): void {
    const descriptor = this.entries.get(runtimeId);
    if (!descriptor) return;
    this.entries.delete(runtimeId);
    this.entries.set(runtimeId, descriptor);
  }

  private prune(): void {
    const protectedIds = new Set<CustomTrackId>([this.activeRuntimeId, this.previousRuntimeId].filter((id): id is CustomTrackId => id !== null));
    while (this.entries.size > MAX_RETAINED_CUSTOM_TRACKS) {
      const candidate = [...this.entries.keys()].find((runtimeId) => !protectedIds.has(runtimeId));
      if (!candidate) break;
      this.release(candidate);
    }
  }

  private release(runtimeId: CustomTrackId): void {
    if (!this.entries.delete(runtimeId)) return;
    const shared = sharedRuntimeEntries.get(runtimeId);
    if (!shared) return;
    shared.references -= 1;
    if (shared.references > 0) return;
    sharedRuntimeEntries.delete(runtimeId);
    if (shared.ownedByDistribution) unregisterCustomTrack(runtimeId);
  }
}

